<template>
  <div class="page equipments">
    <ErrorAlert :message="errorMessage" @clear-error="clearMessage" />
    <SuccessAlert :message="successMessage" @clear-error="clearMessage" />

    <PageHeader title="Echipamente" :subtitle="subtitle" icon="mdi-desktop-classic">
      <template #actions>
        <v-btn
          v-if="canManageCatalog"
          class="btn-ghost-light"
          :to="{ path: '/configurare', query: { tab: 'catalog' } }"
        >
          <v-icon left>mdi-tag-multiple-outline</v-icon>Catalog modele
        </v-btn>
        <TransferEquipmentDialog
          v-if="isAdmin || isEngineer || isDtiSef"
          ref="transferEquipmentDialog"
          @updateEquipmentData="fetchEquipmentData"
        />
        <CreateEquipment
          ref="createEquipment"
          @updateEquipmentData="fetchEquipmentData"
        />
      </template>
      <DepartmentFilter
        v-model="department"
        :items="equipmentData"
        field="ownerDepartment"
        :departments="myDepartments"
      />
    </PageHeader>

    <div class="page-body">
      <section class="panel">
        <div class="panel-toolbar">
          <h2 class="panel-title">
            {{ department || "Toate echipamentele" }}
            <span class="count">{{ filteredData.length }}</span>
          </h2>
          <span class="grow-spacer" />
          <SearchBar @search="handleSearch" />
          <ColumnSelect :headers="headers" :updateVisibleColumns="updateVisibleColumns" pref-key="equipments" />
        </div>
        <EquipmentBulkBar
          :selected="stickerRows"
          :hidden-count="hiddenSelectedCount"
          :department="department"
          :search="search"
          @clear-selection="selectedRows = []"
          @imported="onImported"
          @error="handleError"
        />
        <v-data-table
          v-model="selectedRows"
          :show-select="canSelect"
          item-key="id"
          :mobile-breakpoint="0"
          :headers="visibleHeaders"
          :items="filteredData"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :loading="isLoading"
          @pagination="pageStart = $event.pageStart"
          loading-text="Se încarcă echipamentele..."
          no-data-text="Nu există echipamente"
        >
          <template v-slot:item="{ item, index, isSelected, select }">
            <tr :class="{ 'light-green-row': index % 2 !== 0 }">
              <td v-if="canSelect" class="select-cell">
                <v-simple-checkbox :value="isSelected" color="#2d8659" :ripple="false" aria-label="Selectează" @input="select($event)" />
              </td>
              <td
                v-for="(header, hIndex) in visibleHeaders"
                :key="hIndex"
                :style="header.style ? header.style : {}"
              >
                <div v-if="header.value === 'actions'" class="row-actions">
                  <v-btn
                    v-if="item.requestDepartment"
                    icon
                    color="primary"
                    aria-label="Creează demers"
                    title="Creează demers pentru acest echipament"
                    @click="fileRequest(item)"
                  >
                    <v-icon>mdi-file-document-edit-outline</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="item.tag"
                    icon
                    aria-label="Fișa echipamentului"
                    title="Fișa echipamentului: fotografii, demersuri, comentarii"
                    :to="{ name: 'itemCard', params: { tag: item.tag } }"
                  >
                    <v-icon>mdi-card-account-details-outline</v-icon>
                  </v-btn>
                  <v-btn v-if="canSeeHistory" icon aria-label="Istoric" title="Istoricul echipamentului" @click="showHistory(item)">
                    <v-icon>mdi-history</v-icon>
                  </v-btn>
                  <template v-if="isAdmin || isEngineer || isDtiSef">
                    <v-btn icon color="primary" aria-label="Editează" @click="editItem(item)">
                      <v-icon>mdi-pencil-outline</v-icon>
                    </v-btn>
                    <v-btn icon color="error" aria-label="Șterge" @click="deleteItem(item)">
                      <v-icon>mdi-delete-outline</v-icon>
                    </v-btn>
                  </template>
                </div>
                <span v-else-if="header.value === 'equipmentType'" class="type-cell">
                  <v-icon small class="type-cell__icon">{{ typeIcon(item.equipmentType) }}</v-icon>{{ item.equipmentType }}
                </span>
                <template v-else>
                  {{ getTableCellValue(item, header, index) }}
                </template>
              </td>
            </tr>
          </template>
        </v-data-table>
      </section>
    </div>

    <EditEquipmentPopup
      ref="editEquipmentPopup"
      :equipment="selectedEquipment"
      :visible="showEditPopup"
      @updateEquipmentData="fetchEquipmentData"
    />
    <DeleteItem
      ref="deleteItem"
      :equipment="selectedEquipment"
      :visible="dialogDelete"
      @updateEquipmentData="fetchEquipmentData"
    />
    <EquipmentHistoryDialog ref="historyDialog" />
    <!-- „Creează demers” on a row: the usual Demer nou dialog, on that item -->
    <DialogForm v-if="requestDialogUsed" ref="requestDialog" @error="handleError" @success="handleSuccess" />
  </div>
</template>

<script>
import CreateEquipment from "./CreateEquipment.vue";
import TransferEquipmentDialog from "./TransferEquipmentDialog.vue";
import * as api from "./api";
import * as helper from "@/helper/helper.js";
import DeleteItem from "./DeleteItem.vue";
import EditEquipmentPopup from "./EditEquipmentPopup.vue";
import EquipmentHistoryDialog from "./EquipmentHistoryDialog.vue";
import DialogForm from "../requests/DialogForm.vue";
import { typeIcon } from "./pickers";
import PageHeader from "@/components/layout/PageHeader.vue";
import DepartmentFilter from "@/components/elements/DepartmentFilter.vue";
import { fetchEquipmentForRole } from "../requests/scope";
import ColumnSelect from "@/components/elements/ColumnSelect.vue";
import SearchBar from "@/components/elements/SearchBar.vue";
import ErrorAlert from "@/components/alerts/ErrorAlert.vue";
import SuccessAlert from "@/components/alerts/SuccessAlert.vue";
import EquipmentBulkBar from "./EquipmentBulkBar.vue";
import { mapGetters } from "vuex";
import { CATALOG_MANAGER_ROLES } from "@/config/periferice";
import { sortItems } from "vuetify/lib/util/helpers";

// photo ids and sizes are no search text (server twin: equipmentExport.service.ts SEARCH_IGNORED)
const SEARCH_IGNORED = ["place_file", "label_file"];

const asList = (v) => (Array.isArray(v) ? v : v === undefined || v === null || v === "" ? [] : [v]);

export default {
  name: "EquipmentsPage",
  components: {
    CreateEquipment,
    TransferEquipmentDialog,
    DeleteItem,
    EditEquipmentPopup,
    EquipmentHistoryDialog,
    DialogForm,
    PageHeader,
    DepartmentFilter,
    ColumnSelect,
    SearchBar,
    SuccessAlert,
    ErrorAlert,
    EquipmentBulkBar,
  },
  data() {
    return {
      headers: [],
      equipmentData: [],
      department: "",
      visibleHeaders: [],
      selectedEquipment: null,
      requestDialogUsed: false,
      dialogDelete: false,
      showEditPopup: false,
      search: "",
      sortBy: "id",
      sortDesc: true,
      isLoading: true,
      // first row of the current table page, so "Nr" continues across pages
      pageStart: 0,
      errorMessage: "",
      successMessage: "",
      // ticked rows (QR stickers)
      selectedRows: [],
    };
  },
  methods: {
    async fetchEquipmentData() {
      try {
        // if (this.isLoading === false) return; // Prevent duplicate API calls
        
        this.equipmentData = await fetchEquipmentForRole(this.getUser);
        this.isLoading = false;
        this.refreshSelection();
      } catch (error) {
        console.error("Error fetching data:", error);
        this.isLoading = false;
      }
    },
    // the ticked rows follow the fresh list: an edited row is replaced, a transferred one becomes its new row (same
    // QR code), a deleted one is dropped
    refreshSelection() {
      if (!this.selectedRows.length) return;
      const byId = new Map(this.equipmentData.map((r) => [r.id, r]));
      const byTag = new Map(this.equipmentData.filter((r) => r.tag).map((r) => [r.tag, r]));
      const seen = new Set();
      this.selectedRows = this.selectedRows
        .map((r) => byId.get(r.id) || (r.tag && byTag.get(r.tag)) || null)
        .filter((r) => r && !seen.has(r.id) && seen.add(r.id));
    },
    editItem(item) {
      this.selectedEquipment = { ...item };
      this.$refs.editEquipmentPopup.show();
    },
    deleteItem(item) {
      this.selectedEquipment = { ...item };
      this.$refs.deleteItem.show();
    },
    showHistory(item) {
      this.$refs.historyDialog.show(item);
    },
    async fileRequest(item) {
      // mounted on first use (it loads types and causes)
      this.requestDialogUsed = true;
      await this.$nextTick();
      this.$refs.requestDialog.openFor({
        id: item.id,
        equipmentTypeId: item.equipmentTypeId,
        model: item.model,
        requestDepartment: item.requestDepartment,
      });
    },
    typeIcon,
    getTableCellValue(item, header, index) {
      if (header.value === "index") return this.pageStart + index + 1;
      if (header.value === "equipmentType") return item.equipmentType;
      if (header.value === "responsable_person") return item.responsable_person;
      if (header.value === "model") return item.model;
      if (header.value === "registration_number")
        return item.registration_number;
      if (header.value === "serial_number") return item.serial_number || "";
      if (header.value === "tag") return item.tag || "";
      if (header.value === "room") return item.room;
      if (header.value === "department") return item.departmentId?.name;
      return "";
    },
    sortTable(header) {
      const result = helper.sortTable(header);
      return result;
    },
    updateVisibleColumns() {
      this.visibleHeaders = this.headers.filter((header) => header.visible);
    },
    handleSearch(searchTerm) {
      this.search = searchTerm;
    },
    handleError(message) {
      this.errorMessage = message;
    },
    handleSuccess(message) {
      this.successMessage = message;
    },
    onImported(result) {
      this.fetchEquipmentData();
      const n = Number(result.imported) || 0;
      this.handleSuccess(n === 1 ? "A fost importat 1 echipament" : `Au fost importate ${n >= 20 ? `${n} de` : n} echipamente`);
    },
    clearMessage() {
      this.errorMessage = "";
      this.successMessage = "";
    },
  },
  computed: {
    ...mapGetters(["getUser"]),
    isAdmin() {
      return this.$store.getters.getUser.role === "admin";
    },
    isEquipmentAdmin() {
      return this.$store.getters.getUser.role === "equipment_administrator";
    },
    isEngineer() {
      return this.$store.getters.getUser.role === "inginer" || this.$store.getters.getUser.role === "inginer-pc";
    },
    isEngineerPC() {
      return this.$store.getters.getUser.role === "inginer-pc";
    },
    isDtiSef() {
      return this.$store.getters.getUser.role === "dtisef";
    },
    isRector() {
      return this.$store.getters.getUser.role === "rector";
    },
    // ticking rows prints their QR stickers (server EQUIPMENT_MANAGERS)
    canSelect() {
      return this.isAdmin || this.isEngineer || this.isDtiSef;
    },
    canManageCatalog() {
      return CATALOG_MANAGER_ROLES.includes(this.$store.getters.getUser.role);
    },
    // same people who see the whole equipment park (server: EQUIPMENT_OVERVIEW_ROLES)
    canSeeHistory() {
      return this.isAdmin || this.isEngineer || this.isDtiSef || this.isRector;
    },
    myDepartments() {
      return this.$store.getters.myDepartments;
    },
    subtitle() {
      if (this.isAdmin || this.isEngineer || this.isDtiSef || this.isRector) {
        return "Tot parcul de echipamente al universității.";
      }
      return this.myDepartments.length > 1
        ? "Echipamentele tuturor departamentelor dvs."
        : "Echipamentele departamentului dvs.";
    },
    filteredData() {
      const rows = this.department
        ? this.equipmentData.filter((item) => item.ownerDepartment === this.department)
        : this.equipmentData;
      return helper.filteredData(rows, this.search, SEARCH_IGNORED);
    },
    // ticked rows in the order the table shows them (its sort; Vuetify itself hands them over by id); ticked rows
    // a filter hides now come last
    stickerRows() {
      if (!this.selectedRows.length) return [];
      const shown = sortItems(this.filteredData.slice(), asList(this.sortBy), asList(this.sortDesc), "en-US");
      const position = new Map(shown.map((r, i) => [r.id, i]));
      const at = (r) => (position.has(r.id) ? position.get(r.id) : shown.length);
      return this.selectedRows.slice().sort((a, b) => at(a) - at(b));
    },
    hiddenSelectedCount() {
      if (!this.selectedRows.length) return 0;
      const shown = new Set(this.filteredData.map((r) => r.id));
      return this.selectedRows.filter((r) => !shown.has(r.id)).length;
    },
  },
  mounted() {
    this.headers = require("./headers.json");
    // everyone gets the actions column (item card, „Creează demers”; staff also edit / history);
    // several departments add "Departament"
    const many = this.myDepartments.length > 1;
    this.headers.forEach((header) => {
      header.visible = header.defaultVisible
        || header.value === "actions"
        || (many && header.value === "department");
    });
    this.visibleHeaders = this.headers.filter((header) => header.visible);
    
    // Register event listeners
    this.$refs.deleteItem.$on("error", this.handleError);
    this.$refs.deleteItem.$on("success", this.handleSuccess);
    this.$refs.editEquipmentPopup.$on("error", this.handleError);
    this.$refs.editEquipmentPopup.$on("success", this.handleSuccess);
    this.$refs.createEquipment.$on("error", this.handleError);
    this.$refs.createEquipment.$on("success", this.handleSuccess);
    
    // Fetch data once
    this.$nextTick(() => {
      this.fetchEquipmentData();
    });
  },
};
</script>

<style scoped>
.type-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}
.type-cell__icon.v-icon {
  color: var(--green);
}
.row-actions {
  white-space: nowrap;
}
.select-cell {
  width: 1px;
}
</style>
